import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getClientFieldSettings } from "src/apis/clientFieldSettingApi";

export interface ClientFieldSettingView {
  screenKey: number;
  screenName: string;
  sectionKey: number;
  sectionName: string;
  screenSectionId: number;
  fieldKey: number | null;
  fieldName: string;
  screenFieldId: number | null;
  isFieldVisible: boolean;
  isSectionVisible: boolean;
}

export interface ClientFieldSettingsState {
  settings: ClientFieldSettingView[];
  loading: boolean;
  error: string | null;
}

const initialState: ClientFieldSettingsState = {
  settings: [],
  loading: false,
  error: null,
};

// Async thunk to fetch client field settings
export const fetchClientFieldSettings = createAsyncThunk(
  "clientFieldSettings/fetch",
  async (clientId: number, { rejectWithValue }) => {
    try {
      const response = await getClientFieldSettings(clientId);
      if (response.status !== 200) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create the slice
const clientFieldSettingsSlice = createSlice({
  name: "clientFieldSettings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientFieldSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientFieldSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchClientFieldSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const selectFieldSettings = (state: any) => state.clientFieldSettings.settings;

export const selectIsFieldVisible = (state: any, fieldKey: number) => {
  const setting = state.clientFieldSettings.settings.find(
    (s: ClientFieldSettingView) => s.fieldKey === fieldKey
  );
  return setting ? setting.isFieldVisible : true;
};

export const selectIsSectionVisible = (state: any, sectionKey: number) => {
  const setting = state.clientFieldSettings.settings.find(
    (s: ClientFieldSettingView) => s.sectionKey === sectionKey
  );
  return setting ? setting.isSectionVisible : true;
};

export default clientFieldSettingsSlice.reducer;
